import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Button,
  Text,
  Heading,
  Container,
  VStack,
  HStack,
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  RadioGroup,
  Radio,
  Stack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Divider,
  useColorModeValue,
  FormControl,
  FormLabel,
  Textarea,
  Link,
} from '@chakra-ui/react';
import { DownloadIcon, AttachmentIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';

const PayerResponseGenerator = () => {
  const { caseId: urlCaseId, claimId: urlClaimId } = useParams();
  const { accessToken } = useContext(AppContext);
  
  // State management
  const [caseId, setCaseId] = useState('');
  const [selectedClaimType, setSelectedClaimType] = useState('pro');
  const [caseInfo, setCaseInfo] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  const [isLoadingCaseInfo, setIsLoadingCaseInfo] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generatedResponse, setGeneratedResponse] = useState(null);
  const [textContent, setTextContent] = useState('');

  // Colors for consistent styling
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const accentColor = useColorModeValue('blue.500', 'blue.300');

  // Helper function to clean and format markdown text
  const cleanMarkdownText = (text) => {
    if (!text) return '';
    return text.replace(/\n\s+/g, '\n\n').replace(/\n{3,}/g, '\n\n').trim();
  };

  // Link renderer for ReactMarkdown
  const LinkRenderer = ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" color={accentColor}>
      {children}
    </Link>
  );

  // Get case ID from URL on mount
  useEffect(() => {
    if (urlCaseId) {
      setCaseId(urlCaseId);
      
      if (urlClaimId && (urlClaimId === 'pro' || urlClaimId === 'tech')) {
        setSelectedClaimType(urlClaimId);
      }
    } else {
      setError('No case ID provided in URL');
    }
  }, [urlCaseId, urlClaimId]);

  // Fetch case data when caseId changes
  useEffect(() => {
    if (caseId && accessToken) {
      fetchCaseInfo();
      fetchCaseFiles();
    }
  }, [caseId, accessToken]);

  const fetchCaseInfo = async () => {
    setIsLoadingCaseInfo(true);
    setError(null);
    
    try {
      const response = await callApi(`/get_case_carrier_info/${caseId}`, accessToken);
      const data = await response.json();
      
      if (data.status === 'success') {
        setCaseInfo(data.data);
      } else {
        setError(data.error || 'Failed to fetch case information');
      }
    } catch (error) {
      console.error('Error fetching case info:', error);
      setError('Failed to fetch case information');
    } finally {
      setIsLoadingCaseInfo(false);
    }
  };

  const fetchCaseFiles = async () => {
    setIsLoadingFiles(true);
    
    try {
      const response = await callApi(`/get_case_details/${caseId}`, accessToken);
      const data = await response.json();
      
      if (response.ok) {
        setCaseFiles(data.files || []);
      } else {
        console.error('Failed to fetch case files:', data.error);
      }
    } catch (error) {
      console.error('Error fetching case files:', error);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleFileUpload = async () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      setIsUploading(true);
      setError(null);

      try {
        for (const file of files) {
          const formData = new FormData();
          formData.append('file_type_id', '1');
          formData.append('claim_id', selectedClaimType);
          formData.append('file', file);

          const response = await callApi(`/upload_file/${caseId}`, accessToken, {
            method: 'POST',
            body: formData,
          });

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || `Failed to upload ${file.name}`);
          }
        }

        await fetchCaseFiles();
        alert(`Successfully uploaded ${files.length} file(s) for ${selectedClaimType === 'pro' ? 'Professional' : 'Technical'} claim`);
      } catch (error) {
        console.error('Error uploading files:', error);
        setError(`Upload failed: ${error.message}`);
      } finally {
        setIsUploading(false);
      }
    };
    fileInput.click();
  };

  const handleTextUpload = async () => {
    if (!textContent.trim()) {
      alert('Please enter some text content to upload');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file_type_id', '1');
      formData.append('claim_id', selectedClaimType);
      
      const blob = new Blob([textContent], { type: 'text/plain' });
      formData.append('file', blob, 'pasted_content.txt');

      const response = await callApi(`/upload_content/${caseId}`, accessToken, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        setTextContent('');
        await fetchCaseFiles();
        alert(`Successfully uploaded text content for ${selectedClaimType === 'pro' ? 'Professional' : 'Technical'} claim`);
      } else {
        throw new Error(data.error || 'Failed to upload text content');
      }
    } catch (error) {
      console.error('Error uploading text content:', error);
      setError(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateResponse = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedResponse(null);

    try {
      const response = await callApi('/payer_documents/generate-response', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          claim_id: selectedClaimType
        }),
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setGeneratedResponse(data);
      } else {
        setError(data.message || 'Failed to generate response');
      }
    } catch (error) {
      console.error('Error generating response:', error);
      setError('Failed to generate response. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseInfo?.id || caseInfo?.case_info?.id,
          file_id: file.id
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = file.file_name;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to download file');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(`Download failed: ${error.message}`);
    }
  };

  // Filter files by claim type
  const getFilesByClaimType = (claimType) => {
    return caseFiles.filter(file => file.claim_id === claimType);
  };

  const proFiles = getFilesByClaimType('pro');
  const techFiles = getFilesByClaimType('tech');

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={6}>
        <VStack spacing={6} align="stretch">
          <Heading size="lg">Payer Response Generator - Case {caseId}</Heading>

          {/* Error Display */}
          {error && (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Error:</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Claim Type Selection */}
          <Card>
            <CardHeader>
              <Heading size="md">Select Claim Type</Heading>
            </CardHeader>
            <CardBody>
              <RadioGroup value={selectedClaimType} onChange={setSelectedClaimType}>
                <Stack direction="row" spacing={6}>
                  <Radio value="pro">Professional</Radio>
                  <Radio value="tech">Technical</Radio>
                </Stack>
              </RadioGroup>
            </CardBody>
          </Card>

          {/* Case Information */}
          <Card>
            <CardHeader>
              <Heading size="md">Case Information</Heading>
            </CardHeader>
            <CardBody>
              {isLoadingCaseInfo ? (
                <Flex justify="center" py={4}>
                  <Spinner />
                </Flex>
              ) : caseInfo ? (
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <Text><strong>Patient:</strong> {caseInfo.patient_name || 'N/A'}</Text>
                  </GridItem>
                  <GridItem>
                    <Text><strong>DOB:</strong> {caseInfo.patient_dob || 'N/A'}</Text>
                  </GridItem>
                  <GridItem>
                    <Text><strong>Insurance:</strong> {caseInfo.insurance_name || 'N/A'}</Text>
                  </GridItem>
                  <GridItem>
                    <Text><strong>Policy:</strong> {caseInfo.policy_number || 'N/A'}</Text>
                  </GridItem>
                </Grid>
              ) : (
                <Text>No case information available</Text>
              )}
            </CardBody>
          </Card>

          {/* File Upload Section */}
          <Card>
            <CardHeader>
              <Heading size="md">Upload Payer Documents</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack spacing={4}>
                  <Button
                    leftIcon={<AttachmentIcon />}
                    onClick={handleFileUpload}
                    isLoading={isUploading}
                    loadingText="Uploading..."
                  >
                    Upload Files
                  </Button>
                  <Text fontSize="sm" color="gray.600">
                    Upload payer response documents for {selectedClaimType === 'pro' ? 'Professional' : 'Technical'} claim
                  </Text>
                </HStack>

                <Divider />

                <FormControl>
                  <FormLabel>Or paste text content:</FormLabel>
                  <Textarea
                    value={textContent}
                    onChange={(e) => setTextContent(e.target.value)}
                    placeholder="Paste payer response text here..."
                    rows={6}
                  />
                  <Button
                    mt={2}
                    size="sm"
                    onClick={handleTextUpload}
                    isLoading={isUploading}
                    isDisabled={!textContent.trim()}
                  >
                    Upload Text Content
                  </Button>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Uploaded Files Display */}
          <Card>
            <CardHeader>
              <Heading size="md">Uploaded Files</Heading>
            </CardHeader>
            <CardBody>
              {isLoadingFiles ? (
                <Flex justify="center" py={4}>
                  <Spinner />
                </Flex>
              ) : (
                <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                  <GridItem>
                    <Heading size="sm" mb={3}>Professional Files ({proFiles.length})</Heading>
                    {proFiles.length > 0 ? (
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>File Name</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {proFiles.map((file) => (
                            <Tr key={file.id}>
                              <Td>{file.file_name}</Td>
                              <Td>
                                <Button
                                  size="xs"
                                  leftIcon={<DownloadIcon />}
                                  onClick={() => handleFileDownload(file)}
                                >
                                  Download
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    ) : (
                      <Text fontSize="sm" color="gray.500">No professional files uploaded</Text>
                    )}
                  </GridItem>

                  <GridItem>
                    <Heading size="sm" mb={3}>Technical Files ({techFiles.length})</Heading>
                    {techFiles.length > 0 ? (
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>File Name</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {techFiles.map((file) => (
                            <Tr key={file.id}>
                              <Td>{file.file_name}</Td>
                              <Td>
                                <Button
                                  size="xs"
                                  leftIcon={<DownloadIcon />}
                                  onClick={() => handleFileDownload(file)}
                                >
                                  Download
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    ) : (
                      <Text fontSize="sm" color="gray.500">No technical files uploaded</Text>
                    )}
                  </GridItem>
                </Grid>
              )}
            </CardBody>
          </Card>

          {/* Generate Response Section */}
          <Card>
            <CardHeader>
              <Heading size="md">Generate Appeal Response</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm">
                  Generate an automated appeal response for the {selectedClaimType === 'pro' ? 'Professional' : 'Technical'} component
                  based on uploaded payer documents and case information.
                </Text>
                <Box>
                  <Button
                    colorScheme="green"
                    size="md"
                    onClick={handleGenerateResponse}
                    isLoading={isGenerating}
                    loadingText="Generating Response..."
                    isDisabled={!caseId}
                  >
                    Generate Appeal Response for {selectedClaimType === 'pro' ? 'Professional' : 'Technical'}
                  </Button>
                </Box>
              </VStack>
            </CardBody>
          </Card>


{/* Generated Response Display */}
{generatedResponse && (
  <Card>
    <CardHeader>
      <Heading size="md">Generated Appeal Response</Heading>
    </CardHeader>
    <CardBody>
      <Box 
        p={4} 
        border="1px" 
        borderColor={borderColor} 
        borderRadius="md"
        bg={cardBg}
        whiteSpace="pre-wrap"
      >
        <ReactMarkdown
          components={{
            a: LinkRenderer,
          }}
        >
          {cleanMarkdownText(generatedResponse.generated_content?.appeal_letter)}
        </ReactMarkdown>
      </Box>
    </CardBody>
  </Card>
)}









        </VStack>
      </Container>
    </Box>
  );
};

export default PayerResponseGenerator;